'use client'

import React, { useEffect, useRef, useState } from 'react'
import { getClassNames } from '@helpers/productWidgetHelper'
import { useAnimateOnVisible } from '@hooks/useAnimateOnVisible'
import { CustomerStory } from '@interfaces/brandHub'
import { Container, ContainerProps, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Avatar } from '@quark/avatar'
import RatingStars from '@components/CustomerStory/RatingStars/RatingStars'
import { ReviewAnimationContants } from '@constants/home'
import styles from './ReviewCarousel.module.scss'

const AnimatedCarousel = ({ inpData }: { inpData: CustomerStory[] }) => {
  const { ref, isVisible } = useAnimateOnVisible<HTMLDivElement>({})
  const [data, setData] = useState(inpData.map((d, i) => ({ ...d, id: i })))
  const [animating, setAnimating] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const tick = () => {
    setAnimating(true)
    timeoutRef.current = setTimeout(() => {
      setData(prev => {
        const newData = [...prev]
        const first = newData.shift()
        if (first) newData.push(first)
        return newData
      })
      setAnimating(false)
    }, ReviewAnimationContants.ANIMATION_DURATION)
  }

  const pauseAnimation = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }

  const playAnimation = () => {
    intervalRef.current = setInterval(tick, ReviewAnimationContants.INTERVAL)
  }

  useEffect(() => {
    if (!isVisible) return
    playAnimation()
    return () => {
      pauseAnimation()
    }
  }, [isVisible, playAnimation])

  const bgColorArray: Array<ContainerProps['backgroundColor']> = [
    'accent/Blue/blue-200',
    'accent/Violet/Violet-200',
    'base/overlay',
    'accent/Green/green-200',
  ]
  const avatatBgColorArray: Array<ContainerProps['backgroundColor']> = [
    'accent/Blue/blue-600',
    'accent/Violet/Violet-600',
    'base/elevated',
    'accent/Green/green-600',
  ]
  return (
    <Flex gap="spacing-24" ref={ref}>
      {data.map((d, i) => (
        <div
          key={`${d.customerName}-${i}`}
          className={getClassNames([styles.review], {
            [styles.reviewExit]: animating && i === 0,
            [styles.reviewEnter]: animating && i !== 0,
          })}>
          <Container
            type="max-parent"
            overflow="hidden"
            borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
            padding={{ lg: 'spacing-32', md: 'spacing-24', sm: 'spacing-20' }}
            backgroundColor={bgColorArray[d.id % bgColorArray.length]}>
            <Flex size="fullHeight" flexDirection="column" justifyContent="space-between">
              <Flex flexDirection="column" gap="spacing-16">
                {/* to quark */}
                {typeof d.rating === 'number' && d.rating > 0 && <RatingStars rating={d.rating} total={5} />}
                <Text
                  fontStyle={{ lg: 'um_text-xl/normal', md: 'um_text-lg/normal', sm: 'um_text-lg/normal' }}
                  color="base/primary">
                  {d.story}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="spacing-12">
                <Avatar size="md" type="circle" backgroundColor={avatatBgColorArray[d.id % bgColorArray.length]}>
                  <Text fontStyle="um_text-xl/normal" color="base/primary">
                    {d.customerName.charAt(0).toUpperCase()}
                  </Text>
                </Avatar>
                <Flex flexDirection="column" gap="spacing-4">
                  <Text
                    fontStyle={{ lg: 'um_text-xl/medium', md: 'um_text-xl/medium', sm: 'um_text-base/medium' }}
                    color="base/primary">
                    {d.customerName}
                  </Text>
                  <Text
                    fontStyle={{ lg: 'um_text-sm/normal', md: 'um_text-sm/normal', sm: 'um_text-xs/normal' }}
                    color="base/secondary">
                    {d.customerTitle}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Container>
        </div>
      ))}
    </Flex>
  )
}

export default AnimatedCarousel
